import { useMemo } from "react";

/**
 * Hand-rolled JSON syntax colouring. Same discipline as the deck: no
 * highlighting library for what a single regex can do.
 */

const TOKEN =
  /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b/g;

/** `_meta` keys and protocol-critical fields are worth the eye's attention. */
const HIGHLIGHT = new Set([
  '"_meta"',
  '"resultType"',
  '"requestState"',
  '"inputRequests"',
  '"inputResponses"',
  '"ttlMs"',
  '"cacheScope"',
  '"protocolVersions"',
  '"io.modelcontextprotocol/protocolVersion"',
  '"io.modelcontextprotocol/clientInfo"',
  '"io.modelcontextprotocol/clientCapabilities"',
  '"io.modelcontextprotocol/serverInfo"',
  '"io.modelcontextprotocol/logLevel"',
  '"io.modelcontextprotocol/subscriptionId"',
]);

export function Json({ value, className = "" }: { value: unknown; className?: string }) {
  const nodes = useMemo(() => {
    const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    if (!text) return null;

    const out: React.ReactNode[] = [];
    let last = 0;
    let i = 0;

    for (const match of text.matchAll(TOKEN)) {
      const start = match.index;
      if (start > last) out.push(text.slice(last, start));

      const [full, key, str, num, lit] = match;
      if (key !== undefined) {
        const emphasised = HIGHLIGHT.has(key);
        out.push(
          <span key={i++} className={emphasised ? "text-warning font-semibold" : "text-primary"}>
            {key}
          </span>,
          full.slice(key.length),
        );
      } else if (str !== undefined) {
        out.push(
          <span key={i++} className="text-success">
            {str}
          </span>,
        );
      } else if (num !== undefined) {
        out.push(
          <span key={i++} className="text-secondary">
            {num}
          </span>,
        );
      } else if (lit !== undefined) {
        out.push(
          <span key={i++} className="text-danger">
            {lit}
          </span>,
        );
      }
      last = start + full.length;
    }
    out.push(text.slice(last));
    return out;
  }, [value]);

  return (
    <pre
      className={`wire-scroll overflow-x-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words text-default-300 ${className}`}
    >
      {nodes}
    </pre>
  );
}
